/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect } from 'react';
import { useBasket } from '@/providers/basket';
import {
    assignStJudeGlobal,
    buildStJudeProductEntriesFromLineItems,
    wrapStJudeGlobal,
} from '@/lib/tracking/stjude-datalayer';
import { useStJudeRootLoaderData } from './use-stjude-root-loader-data';

/**
 * Adds `digitalData.product[]` (one `productInfo` entry per basket line item)
 * on the cart page and the login page (legacy's "cart login step") — the
 * root loader (and `StJudeDataLayerSync`, which explicitly skips these
 * routes — see its doc comment) has no basket data. `page`/`type` for both
 * routes are already correct from the generic mapper
 * (`/s/{siteId}/cart`/`"cart"`, `/s/{siteId}/login`/`"login"`) — this only
 * adds the extra field.
 *
 * Uses `useBasket({ autoLoad: true })` rather than the default read-only
 * `useBasket()`: the login page never loads a basket itself, and while the
 * cart page does (via `CartContent`'s `useBasketUpdater`), auto-load is a
 * no-op once that's already happened (see `useBasket`'s doc comment) — same
 * component works correctly mounted on either route.
 *
 * Mount inside the cart and login page route components. Non-visual (renders nothing).
 */
export function StJudeBasketProductDataLayerSync() {
    const rootData = useStJudeRootLoaderData();
    const basket = useBasket({ autoLoad: true });

    useEffect(() => {
        if (!rootData?.omnitureConfig?.omnitureEnabled || !rootData.stJudeDigitalData) return;

        assignStJudeGlobal(
            wrapStJudeGlobal({
                ...rootData.stJudeDigitalData,
                product: buildStJudeProductEntriesFromLineItems(basket?.productItems ?? []),
            })
        );
    }, [basket, rootData]);

    return null;
}
